<template>
  <div class="d-flex flex-column drawer-container">
    <v-list-item
      v-if="$vuetify.breakpoint.mdAndDown"
      class="photo-header"
    > 
        <v-list-item-content>
          <v-img
            src="img/logo/beige_logo.png"
            contain
          >
          </v-img>
        </v-list-item-content>
      </v-list-item>

    <v-list class="pt-0 flex-list" style="">
      <template v-for="item in navigationItems">
        <v-list-item
          :id="'nav-item-' + item.title"
          v-if="item.show"
          :key="item.title"
          :class="{ 'nav-item--active': item.active }"
          @click="action(item.action)">
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <mrx-translation :token="item.title"></mrx-translation>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-divider
          v-if="item.show"
          :key="item.title + '.divider'"
          class="drawer-section-divider"
        ></v-divider>
      </template>

      <template v-for="group in navigationGroups">
        <v-list-group
          :id="'nav-group-' + group.title"
          :value="openGroup === group.title"
          :key="group.title"
          @input="onGroupToggle(group.title, $event)"
          :no-action="group.noAction"
          @click="testModeClick()"
          v-if="group.show"
          active-class="drawerOpen"
        >
          <template slot="activator">
            <v-list-item-icon>
              <v-icon class="ma-0">{{ group.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-wrap">
                <mrx-translation :token="group.title"></mrx-translation>
              </v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="subItem in group.items"
            :key="subItem.title"
            :class="{ 'language-item--selected': subItem.selected }"
            @click="action(subItem.click)"
            :id="'nav-group-item-' + subItem.title"
            >
            <template v-if="subItem.action">
              <v-list-item-content>
                <v-list-item-title class="text-wrap">
                  <mrx-translation 
                    :showUntranslatedToken="true" 
                    :token="subItem.title"
                    ></mrx-translation>
                  </v-list-item-title>
                </v-list-item-content>
              </template>

            <template v-else>
              <v-list-item-icon v-if="subItem.marker" class="language-marker">
                <v-icon
                  :size="11"
                  :class="{ 'language-marker--inactive': !subItem.selected }"
                >{{ icons.mdiCircle }}</v-icon>
              </v-list-item-icon>
              <v-list-item-avatar v-if="subItem.select">
                <v-switch color="white" disabled v-model="subItem.value" :label="subItem.title"></v-switch>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">
                  <mrx-translation 
                    :showUntranslatedToken="true"
                    :token="subItem.title"
                    ></mrx-translation>
                </v-list-item-title>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list-group>

        <v-divider
          v-if="group.show"
          :key="group.title + '.divider'"
          class="drawer-section-divider"
        ></v-divider>
      </template>

      <template v-for="item in navigationItemsAfterGroups">
        <v-list-item
          :id="'nav-item-' + item.title"
          v-if="item.show"
          :key="item.title"
          :class="{ 'nav-item--active': item.active }"
          @click="action(item.action)"
        >
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              <mrx-translation :token="item.title"></mrx-translation>
            </v-list-item-title>
          </v-list-item-content>
          <v-list-item-icon v-if="item.append">
            <v-icon>{{ item.append }}</v-icon>
          </v-list-item-icon>
        </v-list-item>
        <v-divider
          v-if="item.show"
          :key="item.title + '.divider'"
          class="drawer-section-divider"
        ></v-divider>
      </template>

      <v-list-item id="logout" class="logout-section" :disabled="!online" @click="openLogOut()">
        <v-list-item-icon>
          <v-icon>{{ icons.mdiLogout }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            <mrx-translation
              token="app.nav.menu.account.logout.title"
              :css-classes="['logout-label']"
            ></mrx-translation>
          </v-list-item-title>
          <v-list-item-subtitle v-if="!online" id="logout-offline-hint">
            <v-icon size="14">{{ icons.mdiWifiOff }}</v-icon>
            <mrx-translation token="app.nav.menu.account.logout.offline_hint"></mrx-translation>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <log-out-dialog
      :show="logOutDialog"
      :records="recordsAwaitingUpload"
      @cancel="logOutDialog = false"
      @confirm="confirmLogOut()"
    ></log-out-dialog>
  </div>
</template>

<script>
  import { mapActions as mapPiniaActions, mapState } from 'pinia';
  import { useAuthStore } from '@/stores/auth';
  import { useCollectStore } from '@/stores/collect';
  import { useSavedEncountersStore } from '@/stores/savedEncounters';
  import { useLanguageStore } from '@/stores/language';
  import { useTestModeStore } from '@/stores/testMode';
  import { usePartnerConfigStore } from '@/stores/partnerConfig';
  import { useNavigation } from '@/composables/useNavigation';
  import { useConnectionStatus } from '@/composables/useConnectionStatus';
  import LogOutDialog from '@/components/layouts/LogOutDialog';
  import icons from '@/mixins/icons';
  import mrx from '@/mixins/mrx';
  import './styles/drawer.css';

  export default {
    name: 'drawer',
    components: { LogOutDialog },
    mixins: [icons, mrx],
    data() {
      return {
        counter: 0,
        lastClick: Date.now(),
        openGroup: null,
        logOutDialog: false,
      }
    },
    setup() {
      const { to } = useNavigation();
      const { online } = useConnectionStatus();
      return { navigate: to, online };
    },
    computed: {
      showAccountInfo() {
        return this.getMode('show')
      },
      language() {
        let choices = [{
          title: 'responses.language.en-US.title',
          value: 'en-US',
          marker: true,
          selected: this.selectedLanguage === 'en-US',
          click: { action: 'changeLanguage', params: 'en-US' }
        }]
        try {
          choices = this.languages.map(lang => {
            return {
              title: `responses.${lang.name}.title`,
              value: lang.value,
              marker: true,
              selected: lang.value === this.selectedLanguage,
              click: { action: 'changeLanguage', params: lang.value }
            }
          })
        }
        catch (e) {}
        return choices
      },
      routeName() {
        return this.$route?.name || ''
      },
      onSettingsRoute() {
        return ['Settings', 'UserInterface', 'Device'].includes(this.routeName)
          || (this.routeName === 'Summary' && this.$route?.meta?.collection === 'preference')
      },
      navigationItems() {
        return [
          {
            icon: this.icons.mdiHome,
            title: 'app.nav.menu.home.title',
            action: { action: 'navigate', params: { name: 'Start' } },
            active: this.itemIsActive(this.routeName === 'Start'),
            show: true
          },
          {
            icon: this.icons.mdiAccountGroup,
            title: 'app.nav.menu.encounter.resume.title',
            action: { action: 'navigate', params: { name: 'Encounters' } },
            active: this.itemIsActive(this.routeName === 'Encounters'),
            show: this.hasSaved && Boolean(this.encounterPauseResume)
          }
        ]
      },
      navigationItemsAfterGroups() {
        return [
          {
            icon: this.icons.mdiCog,
            title: 'app.nav.menu.settings.title',
            action: { action: 'navigate', params: { name: 'Settings' } },
            active: this.itemIsActive(this.onSettingsRoute),
            append: this.icons.mdiChevronRight,
            show: true
          }
        ]
      },
      navigationGroups() {
        return [{
            icon: this.icons.mdiAccount,
            title: 'app.nav.menu.account.title',
            items: this.engagements,
            noAction: true,
            show: this.showAccountInfo
          }, {
            icon: this.icons.mdiWeb,
            title: 'app.nav.menu.language.title',
            items: this.language,
            noAction: false,
            show: true
          },
          {
            icon: this.icons.mdiHammerScrewdriver,
            title: 'app.nav.menu.testing.title',
            items: this.modes,
            noAction: true,
            active: false,
            show: this.getMode('show')
          },
        ]
      },
      modes() {
        let modes = this.mode.map(x => {
          return {
            title: x.name,
            value: x.value,
            select: true,
            click: { action: 'setMode', params: x.name }
          }
        })
        return modes
      },
      engagements() {
        let engagements = this.logins().map(x => {
          return {
            title: x.name,
            value: x.token,
            click: { action: 'login', params: x.token }
          }
        })
        return engagements
      },
      ...mapState(useCollectStore, { recordsAwaitingUpload: 'recordsInQueue' }),
      ...mapState(useSavedEncountersStore, ['hasSaved']),
      ...mapState(useLanguageStore, { languages: 'all', rtl: 'rtl', selectedLanguage: 'selected' }),
      ...mapState(useTestModeStore, { getMode: 'get', mode: 'all' }),
      ...mapState(usePartnerConfigStore, ['encounterPauseResume']),
    },
    watch: {
      '$route.name'() {
        this.openGroup = null
      },
    },
    methods: {
      testModeClick() {
        if (Date.now() - this.lastClick > 2000) {
          this.counter = 1
        }
        else {
          this.counter = this.counter + 1
        }
        if (this.counter > 9) {
          this.counter = 0
          this.setMode('show')
        }
        this.lastClick = Date.now()
      },
      itemIsActive(onRoute) {
        return !this.openGroup && onRoute
      },
      onGroupToggle(title, open) {
        this.openGroup = open ? title : null
      },
      async openLogOut() {
        if (!this.online) return
        try {
          await this.recordsInQueue()
        }
        catch (e) {
          console.log(e)
        }
        if (!this.online) return
        this.logOutDialog = true
      },
      confirmLogOut() {
        this.logOutDialog = false
        if (!this.online) return
        this.logout()
      },
      action({ action, params }) {
        if (action === 'navigate') this.openGroup = null
        try {
          if (action === 'setMode') {
            this[action](params)
          }
          else {
            this.$emit('navToggle')
            this[action](params)
          }
        }
        catch (e) {
          console.log(e)
        }
      },
      ...mapPiniaActions(useAuthStore, ['login', 'logout', 'logins']),
      ...mapPiniaActions(useLanguageStore, { changeLanguage: 'set' }),
      ...mapPiniaActions(useTestModeStore, { setMode: 'set' }),
    }
  }
</script>
